import { useEffect, useState } from "react";
import client from "@/lib/api";
import { subscribeToPush } from "@/lib/push";
import { toast } from "sonner";
import { Download, Bell, Smartphone, Calendar, Share, PlusSquare, CheckCircle2, Flag } from "lucide-react";

export default function AppDownload() {
  const [installable, setInstallable] = useState(!!window.__deferredPrompt);
  const [installed, setInstalled] = useState(window.matchMedia("(display-mode: standalone)").matches);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  useEffect(() => {
    const onPrompt = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); toast.success("App installata! La trovi nella schermata Home."); };
    window.addEventListener("pwa-installable", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("pwa-installable", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    const dp = window.__deferredPrompt;
    if (!dp) { toast.info("Usa il menu del browser: 'Aggiungi a schermata Home' / 'Installa app'."); return; }
    dp.prompt();
    const { outcome } = await dp.userChoice;
    if (outcome === "accepted") toast.success("Installazione avviata!");
    window.__deferredPrompt = null;
    setInstallable(false);
  };

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") { toast.error("Notifiche non supportate su questo dispositivo."); return; }
    const perm = await Notification.requestPermission();
    setNotifPerm(perm);
    if (perm !== "granted") { toast.error("Permesso notifiche negato."); return; }
    localStorage.setItem("legend_notif", "1");
    try {
      const ok = await subscribeToPush();
      if (ok) toast.success("Avvisi attivati! Riceverai una notifica quando andiamo in diretta LIVE.");
      else toast.success("Notifiche attivate su questo dispositivo.");
    } catch (e) {
      console.error("Push subscribe failed:", e);
      toast.success("Notifiche attivate su questo dispositivo.");
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const { data: races } = await client.get("/races");
      const next = races.filter((r) => new Date(r.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date))[0];
      const body = next ? `Prossima gara: ${next.name} — ${next.date_label || next.date}` : "Ti avviseremo prima di ogni gara e quando c'è la diretta LIVE.";
      reg.showNotification("Legend Magazine 🏁", { body, icon: "/logo192.png", badge: "/logo192.png", data: { url: "/calendario" } });
    } catch { /* ignore */ }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12" data-testid="app-download-page">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-legend-red mb-3"><Smartphone size={14} /> App Ufficiale</span>
          <h1 className="font-display font-black uppercase text-4xl md:text-5xl leading-none mb-4">L'app di Legend Magazine</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-6">Installa l'app sul tuo telefono per avere il <strong>calendario di tutte le gare</strong> sempre in tasca e ricevere le <strong>notifiche</strong> prima di ogni evento del Legend Team. Nessuno store: si installa direttamente dal sito.</p>

          <div className="flex flex-wrap gap-3">
            {installed ? (
              <span className="inline-flex items-center gap-2 bg-emerald-500 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm"><CheckCircle2 size={18} /> App installata</span>
            ) : (
              <button onClick={install} data-testid="install-app-btn" className="inline-flex items-center gap-2 bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">
                <Download size={18} /> Installa l'app
              </button>
            )}
            <button onClick={enableNotifications} data-testid="enable-notifications-btn" className="inline-flex items-center gap-2 bg-legend-ink hover:bg-black text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">
              <Bell size={18} /> {notifPerm === "granted" ? "Notifiche attive" : "Attiva notifiche"}
            </button>
          </div>
          {!installable && !installed && (
            <p className="text-xs text-slate-500 mt-4">Se il pulsante non avvia l'installazione, usa il menu del browser.</p>
          )}
        </div>

        <div className="relative">
          <div className="mx-auto w-64 h-[520px] rounded-[2.5rem] bg-legend-ink border-8 border-black shadow-2xl overflow-hidden">
            <div className="h-6 bg-black" />
            <div className="p-4 text-white">
              <img src="/logo192.png" alt="Legend Magazine" className="w-16 h-16 rounded-2xl mx-auto mb-3" />
              <p className="text-center font-display font-black uppercase text-sm">Legend Magazine</p>
              <div className="mt-4 space-y-2">
                {["Rallylegend · 8-11 Ott", "Sogliano Slalom · 18 Ott", "Rallye Sanremo · 16-18 Ott"].map((r, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/10 rounded-sm px-3 py-2 text-xs">
                    <Calendar size={14} className="text-legend-yellow" /> {r}
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-legend-red rounded-sm px-3 py-2 text-xs flex items-center gap-2">
                <Bell size={14} /> Nuova notifica: gara tra 3 giorni!
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="grid sm:grid-cols-2 gap-4 mt-12">
        <div className="rounded-sm border border-slate-200 dark:border-slate-800 p-6 bg-white dark:bg-legend-paper">
          <h3 className="font-display font-bold uppercase text-lg mb-3 flex items-center gap-2"><PlusSquare size={18} className="text-legend-red" /> Android / Chrome</h3>
          <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-1 list-decimal pl-5">
            <li>Tocca "Installa l'app" qui sopra, oppure</li>
            <li>Apri il menu ⋮ del browser</li>
            <li>Scegli "Installa app" / "Aggiungi a schermata Home"</li>
          </ol>
        </div>
        <div className="rounded-sm border border-slate-200 dark:border-slate-800 p-6 bg-white dark:bg-legend-paper">
          <h3 className="font-display font-bold uppercase text-lg mb-3 flex items-center gap-2"><Share size={18} className="text-legend-red" /> iPhone / Safari</h3>
          <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-1 list-decimal pl-5">
            <li>Tocca il pulsante Condividi</li>
            <li>Scorri e scegli "Aggiungi a Home"</li>
            <li>Conferma: l'icona Legend apparirà tra le app</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
